import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Paper, Typography, Button, Fade } from "@mui/material";

const LOCATE_TIMEOUT_MS = 6000;
const SETTLE_REMEASURE_MS = [150, 350, 600, 900];
const EASE = "200ms cubic-bezier(0.4, 0, 0.2, 1)";

const ProfileTour = ({ steps, onFinish }) => {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const rafRef = useRef(null);

  const step = steps[index];

  const advance = useCallback(() => {
    if (index >= steps.length - 1) {
      onFinish?.();
    } else {
      setIndex((i) => i + 1);
    }
  }, [index, steps.length, onFinish]);

  useEffect(() => {
    if (!step) return;
    let cancelled = false;
    const locateStart = Date.now();
    const timers = [];
    let settleTimer = null;

    const cancelRaf = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };

    const measure = () => {
      if (cancelled) return;
      const el = document.querySelector(step.selector);
      if (el) setRect(el.getBoundingClientRect());
    };

    const onScroll = () => {
      measure();
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(measure, 120);
    };

    const begin = () => {
      measure();
      const el = document.querySelector(step.selector);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
      window.addEventListener("scroll", onScroll, {
        passive: true,
        capture: true,
      });
      window.addEventListener("scrollend", measure, { capture: true });
      SETTLE_REMEASURE_MS.forEach((d) => timers.push(setTimeout(measure, d)));
    };

    const locate = () => {
      if (cancelled) return;
      if (document.querySelector(step.selector)) {
        begin();
        return;
      }
      if (Date.now() - locateStart > LOCATE_TIMEOUT_MS) {
        advance();
        return;
      }
      rafRef.current = requestAnimationFrame(locate);
    };

    locate();

    return () => {
      cancelled = true;
      cancelRaf();
      if (settleTimer) clearTimeout(settleTimer);
      timers.forEach(clearTimeout);
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("scrollend", measure, { capture: true });
    };
  }, [step, advance]);

  useEffect(() => {
    if (!step) return;
    const remeasure = () => {
      const el = document.querySelector(step.selector);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", remeasure);
    return () => window.removeEventListener("resize", remeasure);
  }, [step]);

  if (!step || !rect) return null;

  const pad = 8;
  const isLast = index >= steps.length - 1;
  const roomBelow = rect.bottom + 190 < window.innerHeight;
  const calloutPos = roomBelow
    ? { top: rect.bottom + 14 }
    : { bottom: window.innerHeight - rect.top + 14 };

  return (
    <Fade in timeout={300}>
      <Box
        sx={{ position: "fixed", inset: 0, zIndex: (t) => t.zIndex.modal + 1 }}
      >
        <Box
          onClick={advance}
          sx={{ position: "fixed", inset: 0, cursor: "pointer" }}
        />

        <Box
          sx={{
            position: "fixed",
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            borderRadius: 2,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)",
            pointerEvents: "none",
            transition: `top ${EASE}, left ${EASE}, width ${EASE}, height ${EASE}`,
          }}
        />

        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            left: 16,
            right: 16,
            maxWidth: 320,
            mx: "auto",
            p: 2,
            borderRadius: 2,
            transition: `top ${EASE}, bottom ${EASE}`,
            ...calloutPos,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            {step.title}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {step.body}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {steps.length > 1 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flex: 1 }}
              >
                {index + 1} of {steps.length}
              </Typography>
            )}
            <Button onClick={advance} variant="contained" sx={{ ml: "auto" }}>
              {isLast ? "Got it" : "Next"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
};

export default ProfileTour;
